/**
 * @design-contract CUR-113 + CUR-206
 *
 * ChatDisplay is the host for the chat surface. Layout dimensions,
 * cluster traversal, runway measurement, scroll-pinning, and stream-
 * phase semantics live in `~/components/chat/design/` — see
 * `src/components/chat/design/README.md` for the design philosophy
 * and the invariants this module is responsible for guarding.
 *
 * INSTRUCTIONS FOR HUMAN OR AI AGENTS EDITING THIS FILE:
 *   - DO NOT inline `px-[NN]`, `gap-y-N`, or `64 / 16 / 20 / 320 / 150
 *     / 60` near layout identifiers. The ESLint rule
 *     `chat-streaming-contract/no-inline-tokens` will reject the
 *     change. Import the token from `~/components/chat/design`
 *     instead.
 *   - DO NOT hand-roll `lastIndexOf` / loop walks for "latest user
 *     message", "active response text segment", or interleave
 *     detection. Use the selectors in `chatClusterSelectors.ts`.
 *   - DO NOT touch the runway / scroll-pin hooks from this file
 *     directly — they own state that has been regressed at least
 *     four times. Edit `useTurnRunway.ts` / `useTurnScrollPin.ts`
 *     instead, and update their tests with `DO NOT REVERT` markers.
 */
import { throttle } from 'lodash'
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useSearchParams } from 'react-router-dom'
import { FolderOpen } from 'lucide-react'
import { CanvasSelectionContext, EditableCanvasSplitScreen } from '~/components/canvas'
import { BookmarkEditorProvider } from '~/components/chat/BookmarksPopover'
import { ChatComposer } from '~/components/chat/ChatComposer'
import { ChatDisplayHeader } from '~/components/chat/ChatDisplayHeader'
import { CanvasFileViewer } from '~/components/chat/CanvasFileViewer'
import { ConversationTaskListCard, LandingTaskListMenu } from '~/components/chat/LandingTaskList'
import { ChatMessage as ChatMessageContent } from '~/components/chat/ChatMessage'
import { AssistantResponse } from '~/components/chat/AssistantResponse'
import { UserMessage } from '~/components/chat/UserMessage'
import { useLandingArtifactPanel } from '~/components/chat/useLandingArtifactPanel'
import { stabilizeStreamingMarkdown } from '~/components/chat/streamingMarkdown'
import { AgentMode, SpeedMode } from '~/components/chat/ModeDropdown'
import type { ChatMessage } from '~/components/chat/chatTypes'
import { computeFeedbackPositions } from '~/components/chat/chatTimeline'
import {
  BOTTOM_PROXIMITY_THRESHOLD_PX,
  MESSAGE_STACK_OUTER_PADDING_CLASS_DESKTOP,
  MESSAGE_STACK_OUTER_PADDING_CLASS_PLUGIN,
  canContinueReveal,
  composerScrollSpacerDefaultHeightPx,
  detectInterleavedTurnFromEntries,
  getActiveResponseTextMessageIndex,
  getLastProgressMessageIndex,
  hasUserMessageAfterLastProgress,
  latestTurnGapClass,
  pinnedUserMessageTopGapPx
} from '~/components/chat/design'
import { useTurnRunway } from '~/components/chat/design/useTurnRunway'
import { useTurnScrollPin } from '~/components/chat/design/useTurnScrollPin'
import type { CanvasItem, ChatSelectionWidget } from '~/components/chat/message'
import { CustomTooltip } from '~/components/common/CustomTooltip'
import { Typography } from '~/components/common/Typography'
import LandingCoderTaskPanel from '~/components/task/LandingCoderTaskPanel'
import LandingDataWeaveTaskPanel from '~/components/task/LandingDataWeaveTaskPanel'
import LandingDocumentationTaskPanel from '~/components/task/LandingDocumentationTaskPanel'
import { useCurrentConversation } from '~/contexts/CurrentConversationContext'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { bridge } from '~/services/bridge'
import type { CanvasContextInfo } from '~/services/chatService'
import type { ContextUploadApiItem } from '~/types/contextUpload'
import { cm } from '~/utils'

export { ChatMarkdown } from '~/components/chat/ChatMarkdown'
export { computeFeedbackPositions } from '~/components/chat/chatTimeline'

interface ChatMessageEntry {
  message: ChatMessage
  index: number
}

interface LingeringActiveProgress {
  turnUserMessageId: string
  entries: ChatMessageEntry[]
}

const SIDE_PANEL_COMPOSER_SCROLL_SPACER_FLOOR_PX = 200
const MINIMIZED_TASK_CHAT_MIN_WIDTH_PX = 520
const SCRATCH_WORKSPACE_PANEL_WIDTH = 'clamp(640px, 48vw, 960px)'
const CHAT_SPLIT_VIEW_GUTTER_CLASS = 'px-10'

// Runway-debug toggle lives inside `useTurnRunway.ts` now. The scroll-
// debug helper below stays here because it instruments call sites
// outside the runway hook (initial-scroll, post-stream snap, etc.).
function isChatScrollDebugEnabled() {
  if (typeof window === 'undefined') return false

  const flag =
    window.localStorage.getItem('chat_debug_scroll') ??
    window.localStorage.getItem('chat_debug_runway') ??
    window.localStorage.getItem('chat_debug_markdown')
  return ['1', 'true', 'on', 'yes'].includes(flag?.toLowerCase() ?? '')
}

function logChatScroll(source: string, payload: Record<string, unknown>) {
  if (!isChatScrollDebugEnabled()) return
  console.warn(`[chat-scroll:${source}]`, payload)
}

function snapshotScrollContainer(container: HTMLElement | null) {
  if (!container) return { scrollTop: null, scrollHeight: null, clientHeight: null, distance: null }
  const distance = container.scrollHeight - container.scrollTop - container.clientHeight
  return {
    scrollTop: container.scrollTop,
    scrollHeight: container.scrollHeight,
    clientHeight: container.clientHeight,
    distance
  }
}

function normalizeTimestampMs(value: unknown): number | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) return null
    return value < 10_000_000_000 ? value * 1000 : value
  }

  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!trimmed) return null

  const numericValue = Number(trimmed)
  if (Number.isFinite(numericValue) && numericValue > 0) {
    return normalizeTimestampMs(numericValue)
  }

  const parsed = Date.parse(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

function getMessageCreatedAtMs(message: ChatMessage | null | undefined): number | null {
  if (!message) return null
  return normalizeTimestampMs((message as unknown as { createdTime?: unknown }).createdTime)
}

function getLatestUserMessageEntry(messages: ChatMessage[]): ChatMessageEntry | null {
  let latestByTimestamp: (ChatMessageEntry & { createdAtMs: number }) | null = null
  let latestByPosition: ChatMessageEntry | null = null

  messages.forEach((message, index) => {
    if (message.role !== 'user') return

    latestByPosition = { message, index }

    const createdAtMs = getMessageCreatedAtMs(message)
    if (createdAtMs === null) return

    if (
      latestByTimestamp === null ||
      createdAtMs > latestByTimestamp.createdAtMs ||
      (createdAtMs === latestByTimestamp.createdAtMs && index > latestByTimestamp.index)
    ) {
      latestByTimestamp = { message, index, createdAtMs }
    }
  })

  return latestByTimestamp ?? latestByPosition
}

function hasSelectionWidgetSelection(
  selections: Record<string, string[]>,
  widgetId: string
): boolean {
  return Object.prototype.hasOwnProperty.call(selections, widgetId)
}

function getValidSelectionWidgetValues(
  widget: ChatSelectionWidget,
  selectedValues: string[]
): string[] {
  const optionValues = new Set(widget.options.map(option => option.value))
  const validValues = selectedValues.filter(value => optionValues.has(value))
  return widget.selectionMode === 'SINGLE_SELECT' ? validValues.slice(0, 1) : validValues
}

function formatSelectionWidgetDraft(widget: ChatSelectionWidget, selectedValues: string[]): string {
  const validSelectedValues = getValidSelectionWidgetValues(widget, selectedValues)
  if (validSelectedValues.length === 0) return ''

  const selectedValueSet = new Set(validSelectedValues)
  const orderedValues = widget.options
    .filter(option => selectedValueSet.has(option.value))
    .map(option => option.value)
  const values = orderedValues.length > 0 ? orderedValues : validSelectedValues
  const joinedValues = values.join(', ')
  const prefix = widget.inputPrefix ?? ''
  if (prefix) {
    return `${prefix}${joinedValues}`
  }

  const label = widget.label?.trim()
  return label ? `${label}: ${joinedValues}` : joinedValues
}

function buildSelectionWidgetDraft(
  messages: ChatMessage[],
  selections: Record<string, string[]>,
  disabledWidgetIds: ReadonlySet<string>
): string {
  const drafts: string[] = []
  for (const message of messages) {
    if (message.type !== 'selection_widget') continue
    const widget = message.widget
    if (disabledWidgetIds.has(widget.widgetId)) continue
    const selectedValues = selections[widget.widgetId] ?? []
    const draft = formatSelectionWidgetDraft(widget, selectedValues)
    if (draft) drafts.push(draft)
  }
  return drafts.join('\n')
}

function getDisabledSelectionWidgetIds(messages: ChatMessage[]): Set<string> {
  const disabledWidgetIds = new Set<string>()
  let hasLaterUserWithoutTimestamp = false
  let latestLaterUserCreatedAtMs: number | null = null

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message.role === 'user') {
      const createdAtMs = getMessageCreatedAtMs(message)
      if (createdAtMs === null) {
        hasLaterUserWithoutTimestamp = true
      } else {
        latestLaterUserCreatedAtMs =
          latestLaterUserCreatedAtMs === null
            ? createdAtMs
            : Math.max(latestLaterUserCreatedAtMs, createdAtMs)
      }
      continue
    }

    if (message.type !== 'selection_widget') continue

    const messageCreatedAtMs = getMessageCreatedAtMs(message)
    const hasLaterUserTurn =
      hasLaterUserWithoutTimestamp ||
      (latestLaterUserCreatedAtMs !== null &&
        (messageCreatedAtMs === null || latestLaterUserCreatedAtMs >= messageCreatedAtMs))

    if (hasLaterUserTurn) {
      disabledWidgetIds.add(message.widget.widgetId)
    }
  }

  return disabledWidgetIds
}

/**
 * STREAMING MARKDOWN STABILIZER
 *
 * Auto-closes any unclosed markdown formatting so it renders correctly
 * mid-stream. Instead of hiding content, we append the missing closing
 * markers. When the real closer arrives from the server, it naturally
 * takes over — no content is ever hidden, text streams at full speed.
 *
 * Tables are the exception: incomplete rows and missing separators are
 * held back because you can't meaningfully auto-close a partial table.
 *
 * IMPORTANT: This function is critical for streaming UX. Without it,
 * raw ** ` ~~ markers flash on screen mid-stream before their closers
 * arrive. Do not remove — see streaming markdown and ChatDisplay tests.
 *
 * Design decision: auto-close > truncate. Truncating hides content and
 * fights with the stream buffer (text can shrink). Auto-closing keeps
 * all content visible and formatting correct at every frame.
 */
export function stabilizeMarkdown(text: string): string {
  // Back-compat export for existing tests and any legacy call sites.
  // The canonical implementation lives in components/markdown/streamingMarkdown.ts.
  return stabilizeStreamingMarkdown(text)
}

// Props for the top-level chat display surface.
interface ChatDisplayProps {
  // Data
  messages: ChatMessage[]
  conversationTitle?: React.ReactNode // Changed from string to ReactNode
  conversationSubtitle?: React.ReactNode // Changed from string to ReactNode

  // Behavior
  onSendMessage: (
    message: string,
    executionMode?: string,
    verificationMode?: string,
    contextUpload?: ContextUploadApiItem[],
    canvasContextInfo?: CanvasContextInfo[],
    isSpeechInput?: boolean
  ) => Promise<void> | void
  isLoading?: boolean
  onFeedbackUpdate?: (
    messageId: string,
    feedback: 'positive' | 'negative' | undefined,
    feedbackReason?: string,
    otherReason?: string
  ) => void
  scratchCanvasItem?: CanvasItem

  // UI Configuration
  inputPlaceholder?: string

  // Streaming support (for thinking)
  streamingEvents?: AsyncIterable<unknown> | null
}

export const ChatDisplay: React.FC<ChatDisplayProps> = ({
  messages,
  conversationTitle,
  conversationSubtitle,
  onSendMessage,
  isLoading = false,
  inputPlaceholder = 'Type your message...',
  onFeedbackUpdate,
  scratchCanvasItem
}) => {
  const isPlugin = bridge.isInPlugin()
  const [messagesContainerRef, setMessagesContainerRef] = useState<HTMLDivElement | null>(null)
  // Mirror the scroll container element into a ref so stable callbacks
  // (throttled scroll, scrollToBottom, etc.) can read the latest container
  // without being recreated whenever the state ref changes.
  const messagesContainerElRef = useRef<HTMLDivElement | null>(null)
  messagesContainerElRef.current = messagesContainerRef
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const userHasScrolledUpRef = useRef(false)
  // Note: `lastUserScrollIntentMsRef` + `noteUserScrollIntent` previously
  // lived here. They're now owned by `useTurnScrollPin` (below). The
  // ref's purpose is documented inside that hook.
  // Distance (in px) from the absolute bottom that we still consider "at the
  // bottom". Imported from `chat/design/chatLayoutTokens.ts` so the
  // recompute below stays in sync with the onScroll handler at the bottom
  // of the component.
  // ───────────────────────────────────────────────────────────────────────
  // INVARIANT (FRAGILE): Every "if user is at the bottom, snap to bottom"
  // code path MUST call `updateUserScrolledUpFromCurrentLayout()` instead of
  // reading `userHasScrolledUpRef.current` directly.
  // ───────────────────────────────────────────────────────────────────────
  // `userHasScrolledUpRef` is only updated from the `onScroll` handler, so
  // it goes stale whenever the scroll content grows (streaming response,
  // newly mounted progress disclosure, image lazy-load, etc.) without the
  // user touching the wheel/trackpad — `scrollTop` doesn't change, no
  // scroll event fires, the ref stays "at bottom" while the user is now
  // hundreds of pixels from the bottom. A throttledScrollToBottom that
  // trusts the stale ref will yank the reader down mid-stream. The live
  // recompute below reads `scrollHeight/scrollTop/clientHeight` against
  // the current DOM, so it's always correct.
  //
  // If you find a new auto-scroll path, route it through this helper.
  const updateUserScrolledUpFromCurrentLayout = useCallback(() => {
    const container = messagesContainerElRef.current
    if (!container) return userHasScrolledUpRef.current
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight
    const hasScrolledUp = distance > BOTTOM_PROXIMITY_THRESHOLD_PX
    userHasScrolledUpRef.current = hasScrolledUp
    return hasScrolledUp
  }, [])
  const chatLayoutRef = useRef<HTMLDivElement>(null)
  const taskPanelLayoutRef = useRef<HTMLDivElement>(null)
  const composerPanelRef = useRef<HTMLDivElement>(null)
  const latestUserMessageRef = useRef<HTMLDivElement>(null)
  const latestTurnUserMessageRef = useRef<HTMLDivElement>(null)
  const activeProgressSlotRef = useRef<HTMLDivElement>(null)
  const activeResponseSlotRef = useRef<HTMLDivElement>(null)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [composerPanelHeight, setComposerPanelHeight] = useState(0)
  const [composerFrameStyle, setComposerFrameStyle] = useState<React.CSSProperties | undefined>()
  const [activeRunwayUserMessageId, setActiveRunwayUserMessageId] = useState<string | null>(null)
  const [activeRunwayUserMessageContent, setActiveRunwayUserMessageContent] = useState<
    string | null
  >(null)
  // Runway / progress-slot / bottom-spacer state is owned by
  // `useTurnRunway` (see `chat/design/useTurnRunway.ts`). It returns a
  // branded RunwayState so consumers cannot substitute literals — every
  // value here comes from the measurement loop.
  const [lingeringActiveProgress, setLingeringActiveProgress] =
    useState<LingeringActiveProgress | null>(null)
  const [activeResponseRevealState, setActiveResponseRevealState] = useState<{
    messageId: string
    isRevealing: boolean
  } | null>(null)
  const [activeResponseRevealRevision, setActiveResponseRevealRevision] = useState(0)
  const initialScrollDoneRef = useRef(false)
  const { uuid: conversationUuid, workspaceUuid } = useParams<{
    uuid: string
    workspaceUuid: string
  }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { conversation, hasMoreMessages, isLoadingMore, loadMoreMessages } =
    useCurrentConversation()
  const [isInGracePeriod, setIsInGracePeriod] = useState(false)
  const graceTimeoutRef = useRef<number | null>(null)
  const prevIsLoadingRef = useRef<boolean>(isLoading)

  // Track canvas paths that are currently being processed by the agent.
  // This state is separate from canvasRefetchTrigger which handles content refresh.
  //
  // Processing lifecycle:
  // 1. User submits feedback → path added to processingCanvasPaths
  // 2. Agent streams response → isLoading=true, processing indicator visible
  // 3. Streaming ends → isLoading=false, clear processingCanvasPaths
  //
  // This design ties processing state to the streaming lifecycle because:
  // - Feedback submission triggers streaming
  // - Agent is "processing" the feedback while streaming
  // - When streaming ends, the agent is done with that feedback round
  const [processingCanvasPaths, setProcessingCanvasPaths] = useState<Set<string>>(new Set())

  // Handle streaming lifecycle transitions (loading → not-loading and vice versa).
  // Single effect to avoid implicit dependency on effect execution order.
  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current

    // Streaming started/resumed: clear grace period.
    if (!wasLoading && isLoading) {
      setIsInGracePeriod(false)
      if (graceTimeoutRef.current !== null) {
        clearTimeout(graceTimeoutRef.current)
        graceTimeoutRef.current = null
      }
    }

    // Streaming just stopped: clear canvas processing state and start grace period.
    if (wasLoading && !isLoading) {
      // Clear canvas processing indicators
      if (processingCanvasPaths.size > 0) {
        setProcessingCanvasPaths(new Set())
      }

      // Enable grace period for conversation refetch (3 seconds)
      setIsInGracePeriod(true)
      if (graceTimeoutRef.current !== null) {
        clearTimeout(graceTimeoutRef.current)
      }
      graceTimeoutRef.current = window.setTimeout(() => {
        setIsInGracePeriod(false)
        graceTimeoutRef.current = null
      }, 3000)
    }

    prevIsLoadingRef.current = isLoading
    return () => {
      if (graceTimeoutRef.current !== null) {
        clearTimeout(graceTimeoutRef.current)
        graceTimeoutRef.current = null
      }
    }
  }, [isLoading, processingCanvasPaths.size])

  // Create a set of taskIds that appear in the current messages array
  // Only tasks in this set should be protected from being marked as deleted during streaming/grace period
  const taskIdsInMessages = useMemo(() => {
    const taskIds = new Set<number>()
    messages.forEach(msg => {
      if (msg.type === 'task' && msg.taskId) {
        taskIds.add(msg.taskId)
      }
    })
    return taskIds
  }, [messages])

  // Process messages to dedup editable canvas items across the full conversation
  // Canvas stays tied to the message where it first appeared
  // Later occurrences are filtered out (display only first)
  const processedMessages = useMemo((): ChatMessage[] => {
    // Track which s3FilePaths we've seen (for editable canvas dedup)
    const seenEditableCanvasPaths = new Set<string>()
    const seenTaskIds = new Set<number>()

    const result: ChatMessage[] = []
    for (const msg of messages) {
      if (msg.type === 'task') {
        if (seenTaskIds.has(msg.taskId)) continue
        seenTaskIds.add(msg.taskId)
      }

      if (msg.type !== 'canvas') {
        result.push(msg)
        continue
      }

      const filteredItems = msg.canvasItems.filter(item => {
        // Non-editable: always keep
        if (!item.editable || !item.s3FilePath) return true

        // Editable: keep only first occurrence
        if (seenEditableCanvasPaths.has(item.s3FilePath)) return false
        seenEditableCanvasPaths.add(item.s3FilePath)
        return true
      })

      // If no items left after filtering, skip this canvas message
      if (filteredItems.length === 0) continue

      result.push({ ...msg, canvasItems: filteredItems })
    }
    return result
  }, [messages])

  // Load from conversation metadata or use defaults
  const getInitialAgentMode = (): AgentMode => {
    if (conversation?.conversation_metadata?.agent_mode) {
      const saved = conversation.conversation_metadata.agent_mode
      if (saved === 'agent' || saved === 'ask') {
        return saved as AgentMode
      }
    }
    return 'agent'
  }

  const getInitialSpeedMode = (): SpeedMode => {
    if (conversation?.conversation_metadata?.speed_mode) {
      const saved = conversation.conversation_metadata.speed_mode
      if (saved === 'fast' || saved === 'verify') {
        return saved as SpeedMode
      }
    }
    return 'verify'
  }

  const [agentMode, setAgentMode] = useState<AgentMode>(getInitialAgentMode)
  const [speedMode, setSpeedMode] = useState<SpeedMode>(getInitialSpeedMode)

  // State for editable canvas split-screen
  const [editableCanvasOpen, setEditableCanvasOpen] = useState(false)
  const [selectedEditableCanvas, setSelectedEditableCanvas] = useState<CanvasItem | null>(null)
  const [isScratchPanelOpen, setIsScratchPanelOpen] = useState(false)
  const composerTaskRequestIdRef = useRef(0)
  const [codeReviewTaskRequest, setCodeReviewTaskRequest] = useState<{
    taskId: number
    requestId: number
  } | null>(null)
  const [feedbackDraftRequest, setFeedbackDraftRequest] = useState<{
    taskId: number
    requestId: number
  } | null>(null)
  const [selectionWidgetSelections, setSelectionWidgetSelections] = useState<
    Record<string, string[]>
  >({})
  const initializedSelectionWidgetIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    setSelectionWidgetSelections({})
    initializedSelectionWidgetIdsRef.current.clear()
  }, [conversationUuid])

  useEffect(() => {
    setSelectionWidgetSelections(prev => {
      let next = prev
      for (const message of processedMessages) {
        if (message.type !== 'selection_widget') continue
        const widget = message.widget
        if (initializedSelectionWidgetIdsRef.current.has(widget.widgetId)) continue
        initializedSelectionWidgetIdsRef.current.add(widget.widgetId)

        const defaultValues = getValidSelectionWidgetValues(
          widget,
          widget.defaultSelectedValues ?? []
        )
        if (defaultValues.length === 0) continue
        if (hasSelectionWidgetSelection(next, widget.widgetId)) continue
        next = { ...next, [widget.widgetId]: defaultValues }
      }
      return next
    })
  }, [processedMessages])

  const disabledSelectionWidgetIds = useMemo(
    () => getDisabledSelectionWidgetIds(processedMessages),
    [processedMessages]
  )

  const selectionWidgetDraft = useMemo(
    () =>
      buildSelectionWidgetDraft(
        processedMessages,
        selectionWidgetSelections,
        disabledSelectionWidgetIds
      ),
    [disabledSelectionWidgetIds, processedMessages, selectionWidgetSelections]
  )

  const handleSelectionWidgetChange = useCallback(
    (widget: ChatSelectionWidget, selectedValues: string[]) => {
      setSelectionWidgetSelections(prev => ({
        ...prev,
        [widget.widgetId]: getValidSelectionWidgetValues(widget, selectedValues)
      }))
    },
    []
  )

  // Handler for editable canvas click - opens split-screen editor
  const handleEditableCanvasClick = useCallback(
    (canvas: CanvasItem) => {
      // Check if user is at the bottom before layout change. Recompute from
      // the live container — the ref alone may be stale after content grew
      // without a scroll event.
      const wasAtBottom = !updateUserScrolledUpFromCurrentLayout()
      setSelectedEditableCanvas(canvas)
      setEditableCanvasOpen(true)
      setIsScratchPanelOpen(false)
      // After canvas opens and content reflows (mr-[700px] applied), keep scroll at bottom
      if (wasAtBottom) {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
        })
      }
    },
    [updateUserScrolledUpFromCurrentLayout]
  )

  // Close split-screen handler
  const handleCloseSplitScreen = useCallback(() => {
    setEditableCanvasOpen(false)
    setSelectedEditableCanvas(null)
  }, [])

  const handleScratchPanelToggle = useCallback(() => {
    if (!scratchCanvasItem) return

    if (isScratchPanelOpen) {
      setIsScratchPanelOpen(false)
      return
    }

    handleCloseSplitScreen()
    setIsScratchPanelOpen(true)
  }, [handleCloseSplitScreen, isScratchPanelOpen, scratchCanvasItem])

  // Handle canvas selection feedback - send user feedback with structured canvas context
  const handleCanvasSelectionFeedback = useCallback(
    (feedback: string, context: CanvasSelectionContext) => {
      // Mark canvas as processing
      setProcessingCanvasPaths(prev => new Set(prev).add(context.s3FilePath))

      // Build canvas context info for the API (structured data separate from message)
      const canvasContextInfo: CanvasContextInfo[] = [
        {
          canvasHash: context.s3FilePath, // Use s3FilePath as the unique identifier
          canvasTitle: context.canvasTitle,
          contextText: context.selectedText,
          contextBefore: context.contextBefore,
          contextAfter: context.contextAfter,
          contextStartLine: context.startLine,
          contextEndLine: context.endLine
        }
      ]

      // Send only the user's feedback as the message, with canvas context as structured data.
      // CUR-210: use the live composer modes (agent/ask, fast/verify) instead of the
      // previous hard-coded DEFAULT_CHAT_* constants so canvas selection feedback respects
      // the user's current mode. Backend accepts all AgentMode / SpeedMode enum values
      // (see conversation.py metadata validation).
      onSendMessage(feedback, agentMode, speedMode, undefined, canvasContextInfo)
    },
    [onSendMessage, agentMode, speedMode]
  )

  // Track last synced conversation UUID to only sync on conversation change
  const lastSyncedConversationUuidRef = useRef<string | undefined>()

  // Sync from metadata only when conversation UUID changes (new conversation or switch)
  useEffect(() => {
    // Only sync if this is a different conversation than last time
    if (conversationUuid === lastSyncedConversationUuidRef.current) {
      return
    }

    if (conversation?.conversation_metadata) {
      const savedAgentMode = conversation.conversation_metadata.agent_mode
      if (savedAgentMode && (savedAgentMode === 'agent' || savedAgentMode === 'ask')) {
        setAgentMode(savedAgentMode as AgentMode)
      }

      const savedSpeedMode = conversation.conversation_metadata.speed_mode
      if (savedSpeedMode && (savedSpeedMode === 'fast' || savedSpeedMode === 'verify')) {
        setSpeedMode(savedSpeedMode as SpeedMode)
      }
    }

    lastSyncedConversationUuidRef.current = conversationUuid
  }, [conversationUuid, conversation?.conversation_metadata])

  useEffect(() => {
    if (!conversation) {
      return
    }
    setIsScratchPanelOpen(false)
  }, [conversation, conversationUuid])

  useEffect(() => {
    if (!scratchCanvasItem) {
      setIsScratchPanelOpen(false)
    }
  }, [scratchCanvasItem])

  useEffect(() => {
    if (!messages.length) {
      return
    }
  }, [messages, conversationUuid])

  // Scroll position management (existing code remains the same)
  const scrollPositionKey = `chat-scroll-${conversationUuid}`

  // User-scroll-intent listeners + follow-along RAF pin — must be wired
  // before scrollToBottom / scrollLatestTurnIntoView so programmatic
  // smooth scrolls can stamp the intent ref and pause the pin.
  const { noteUserScrollIntent } = useTurnScrollPin({
    scrollContainer: messagesContainerRef,
    scrollContainerRef: messagesContainerElRef,
    isLoading
  })

  const scrollToBottom = useCallback(
    (force = false, smooth = false) => {
      if (smooth) {
        noteUserScrollIntent()
      }
      // Use scrollIntoView for more reliable scrolling during streaming
      setTimeout(() => {
        // Skip if user has scrolled up (unless forced, e.g. for initial load).
        // Recompute live distance first because the cached ref can be stale
        // when content grew without a scroll event.
        const scrolledUp = !force && updateUserScrolledUpFromCurrentLayout()
        logChatScroll('scrollToBottom', {
          force,
          smooth,
          scrolledUp,
          ...snapshotScrollContainer(messagesContainerElRef.current)
        })
        if (scrolledUp) {
          return
        }
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' })
        }
      }, 50) // Increased timeout to allow for DOM updates
    },
    [noteUserScrollIntent, updateUserScrolledUpFromCurrentLayout]
  )

  // Throttled scroll to bottom to prevent excessive scroll operations during
  // streaming. The decision to scroll runs INSIDE the throttled callback so
  // any pending trailing call always re-evaluates against the latest layout.
  const throttledScrollToBottom = useRef(
    throttle(
      () => {
        // Skip if user has scrolled away from bottom (recompute live distance
        // from the current scroll container — the cached ref does not
        // reflect content that grew below the viewport without a scroll
        // event, e.g. while a long response is still streaming/revealing).
        const container = messagesContainerElRef.current
        const snapshot = snapshotScrollContainer(container)
        if (container) {
          const distance = container.scrollHeight - container.scrollTop - container.clientHeight
          if (distance > BOTTOM_PROXIMITY_THRESHOLD_PX) {
            userHasScrolledUpRef.current = true
            logChatScroll('throttled.bail-distance', snapshot)
            return
          }
        } else if (userHasScrolledUpRef.current) {
          logChatScroll('throttled.bail-ref', snapshot)
          return
        }
        logChatScroll('throttled.fire', snapshot)
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
        }
      },
      100,
      { leading: false, trailing: true }
    )
  ).current

  // Reset scroll tracking when conversation changes (before paint). The
  // runway state (progress-slot min-height, runway min-height, bottom
  // spacer) is owned by `useTurnRunway` — its own reset effect keyed on
  // `latestUserMessageId` clears those values when the latest turn
  // identity changes (which it does across conversation switches), so
  // we deliberately do NOT reach into runway state from here.
  useLayoutEffect(() => {
    initialScrollDoneRef.current = false
    pinnedActiveUserMessageIdRef.current = null
    setActiveRunwayUserMessageId(null)
    setActiveRunwayUserMessageContent(null)
    userHasScrolledUpRef.current = false
    setShowScrollToBottom(false)
  }, [conversationUuid])

  // Layout constants live in `chat/design/chatLayoutTokens.ts`. The
  // helpers below pick the right value for the current surface (plug-in
  // vs desktop). Other consumers (the runway hook) read their own copies
  // from the same module — DO NOT inline numbers here.
  const surfaceFlags = { isPlugin }
  const composerScrollSpacerHeight =
    composerPanelHeight || composerScrollSpacerDefaultHeightPx(surfaceFlags)
  const pinnedUserMessageTopGap = pinnedUserMessageTopGapPx(surfaceFlags)

  // Cluster traversal lives in `chat/design/chatClusterSelectors.ts`. The
  // `latestUserEntry` selector is timestamp-aware (with a position
  // tiebreaker) so post-stream silent refetches that briefly insert an
  // out-of-order persisted user row don't change which turn we consider
  // "latest". The downstream memos (`priorTaskContinuationMessageIds`,
  // `outOfOrderPriorTurnMessageIds`, etc.) are conversation-scoped and
  // stay inline here.
  const latestUserEntry = useMemo(
    () => getLatestUserMessageEntry(processedMessages),
    [processedMessages]
  )
  const latestUserMessage = latestUserEntry?.message ?? null
  const latestUserMessageId = latestUserMessage?.id ?? null
  const latestUserMessageContent =
    latestUserMessage?.type === 'text' ? latestUserMessage.content : null
  const latestUserIndex = latestUserEntry?.index ?? -1
  const latestUserCreatedAtMs = useMemo(
    () => getMessageCreatedAtMs(latestUserMessage),
    [latestUserMessage]
  )
  const taskCreatedAtById = useMemo(() => {
    const taskMap = new Map<number, number>()
    for (const task of conversation?.tasks ?? []) {
      const createdAt = normalizeTimestampMs(task?.created_ts)
      if (createdAt !== null) {
        taskMap.set(task.task_id, createdAt)
      }
    }
    return taskMap
  }, [conversation?.tasks])
  const priorTaskContinuationMessageIds = useMemo(() => {
    const ids = new Set<string>()
    if (latestUserIndex < 0 || latestUserCreatedAtMs === null) return ids

    const priorTaskIndexes: number[] = []
    processedMessages.forEach((message, index) => {
      if (index <= latestUserIndex || message.type !== 'task') return

      const taskCreatedAt = taskCreatedAtById.get(message.taskId)
      const messageCreatedAt = getMessageCreatedAtMs(message)
      const createdAt = taskCreatedAt ?? messageCreatedAt
      if (createdAt === null || createdAt >= latestUserCreatedAtMs) return

      ids.add(message.id)
      priorTaskIndexes.push(index)
    })

    for (const taskIndex of priorTaskIndexes) {
      for (let index = taskIndex - 1; index > latestUserIndex; index -= 1) {
        const message = processedMessages[index]
        if (message.role !== 'assistant') break
        if (message.type === 'progress_message') {
          ids.add(message.id)
          continue
        }
        if (message.type === 'task' && ids.has(message.id)) continue
        break
      }
    }

    return ids
  }, [latestUserCreatedAtMs, latestUserIndex, processedMessages, taskCreatedAtById])
  const outOfOrderPriorTurnMessageIds = useMemo(() => {
    const ids = new Set<string>()
    if (latestUserIndex < 0 || latestUserCreatedAtMs === null) return ids

    // Persisted rows can briefly arrive out of order after an optimistic/current turn.
    // Keep those older rows visible, but render them before the true latest turn.
    const outOfOrderPriorUserIndexes: number[] = []
    processedMessages.forEach((message, index) => {
      if (index <= latestUserIndex || message.role !== 'user') return

      const messageCreatedAt = getMessageCreatedAtMs(message)
      if (messageCreatedAt !== null && messageCreatedAt < latestUserCreatedAtMs) {
        ids.add(message.id)
        outOfOrderPriorUserIndexes.push(index)
      }
    })

    for (const userIndex of outOfOrderPriorUserIndexes) {
      for (let index = userIndex + 1; index < processedMessages.length; index += 1) {
        const message = processedMessages[index]
        if (message.role === 'user') break
        if (message.role !== 'assistant') continue

        const messageCreatedAt = getMessageCreatedAtMs(message)
        if (messageCreatedAt !== null && messageCreatedAt < latestUserCreatedAtMs) {
          ids.add(message.id)
        }
      }
    }

    return ids
  }, [latestUserCreatedAtMs, latestUserIndex, processedMessages])
  const preLatestContinuationMessageIds = useMemo(
    () => new Set([...priorTaskContinuationMessageIds, ...outOfOrderPriorTurnMessageIds]),
    [outOfOrderPriorTurnMessageIds, priorTaskContinuationMessageIds]
  )
  const preLatestContinuationEntries = useMemo<ChatMessageEntry[]>(
    () =>
      preLatestContinuationMessageIds.size > 0
        ? processedMessages
            .map((message, index) => ({ message, index }))
            .filter(
              ({ message, index }) =>
                index > latestUserIndex && preLatestContinuationMessageIds.has(message.id)
            )
        : [],
    [latestUserIndex, preLatestContinuationMessageIds, processedMessages]
  )
  const activeRunwayUserMessageWasReplaced =
    !!activeRunwayUserMessageId &&
    !processedMessages.some(message => message.id === activeRunwayUserMessageId)
  const latestUserMatchesReplacedActiveRunway =
    activeRunwayUserMessageWasReplaced &&
    activeRunwayUserMessageContent !== null &&
    latestUserMessageContent === activeRunwayUserMessageContent
  // ───────────────────────────────────────────────────────────────────────
  // INVARIANT (FRAGILE): `latestUserMatchesActiveRunway` MUST allow three
  // distinct ways to match — id-equality, replaced-id+content-equality, AND
  // standalone content-equality. Removing the third ("looks redundant
  // because the second already checks content!") re-introduces the
  // post-stream bottom-yank bug for short turns.
  // ───────────────────────────────────────────────────────────────────────
  // The third match (`latestUserContentMatchesActiveRunway`) covers the
  // brief window during the post-stream silent refetch where BOTH the
  // optimistic message AND the persisted message exist in
  // `processedMessages` simultaneously:
  //   • `activeRunwayUserMessageId` still finds its match in the array
  //     (so `wasReplaced` is false → second branch doesn't fire).
  //   • `latestUserMessageId` now points at the persisted row (different
  //     id → first branch doesn't fire).
  //   • Without this third branch, the post-stream auto-scroll effect
  //     stops bailing, sees the user is "near bottom" for a short turn
  //     (distance < 150px), and `throttledScrollToBottom()` yanks the
  //     view down — hiding the user's prompt header.
  // Guarded by: `does not yank short turns to the bottom when optimistic +
  // persisted user rows briefly coexist post-stream` and
  // `still scrolls when a repeated prompt is a new latest turn` (the latter
  // ensures we DON'T over-match and skip legit new-turn scrolls).
  const latestUserContentMatchesActiveRunway =
    !!activeRunwayUserMessageId &&
    activeRunwayUserMessageContent !== null &&
    latestUserMessageContent === activeRunwayUserMessageContent
  const latestUserMatchesActiveRunway =
    !!latestUserMessageId &&
    (activeRunwayUserMessageId === latestUserMessageId ||
      latestUserMatchesReplacedActiveRunway ||
      latestUserContentMatchesActiveRunway)
  const shouldReserveActiveTurnRunway =
    !!latestUserMessageId && (isLoading || latestUserMatchesActiveRunway)
  // `bottomComposerSpacerHeight` is computed below, AFTER the
  // useTurnRunway() hook returns the measured spacer.
  // CUR-206: "active response" is the LATEST assistant text segment in the
  // latest turn — see `getActiveResponseTextMessageIndex` in design module
  // for the full rationale (DO NOT REVERT to first-text-of-turn).
  const activeResponseTextMessageIndex = useMemo(() => {
    if (!shouldReserveActiveTurnRunway) return -1
    // CUR-206: anchor on the LATEST text segment, gated by the latest
    // user-message timestamp so a briefly-coexisting prior-turn text
    // doesn't get picked up during a post-stream silent refetch. See
    // `getActiveResponseTextMessageIndex` in the design module for the
    // base rule; the timestamp filter is layered here because it depends
    // on `latestUserCreatedAtMs` (conversation-scoped state).
    return getActiveResponseTextMessageIndex(processedMessages, {
      latestUserIndex,
      latestUserCreatedAtMs,
      getCreatedAtMs: getMessageCreatedAtMs
    })
  }, [latestUserCreatedAtMs, latestUserIndex, processedMessages, shouldReserveActiveTurnRunway])
  const activeResponseTextMessage =
    activeResponseTextMessageIndex >= 0 ? processedMessages[activeResponseTextMessageIndex] : null
  const activeResponseTextMessageId = activeResponseTextMessage?.id ?? null
  const shouldRenderLatestTurnAssistantArea = shouldReserveActiveTurnRunway && latestUserIndex >= 0
  const latestTurnAssistantEntries = useMemo<ChatMessageEntry[]>(
    () =>
      shouldRenderLatestTurnAssistantArea
        ? processedMessages
            .map((message, index) => ({ message, index }))
            .slice(latestUserIndex + 1)
            .filter(({ message }) => {
              if (message.role !== 'assistant' || priorTaskContinuationMessageIds.has(message.id)) {
                return false
              }

              const messageCreatedAt = getMessageCreatedAtMs(message)
              return (
                latestUserCreatedAtMs === null ||
                messageCreatedAt === null ||
                messageCreatedAt >= latestUserCreatedAtMs
              )
            })
        : [],
    [
      latestUserCreatedAtMs,
      latestUserIndex,
      priorTaskContinuationMessageIds,
      processedMessages,
      shouldRenderLatestTurnAssistantArea
    ]
  )
  const latestTurnAssistantMessageIds = useMemo(
    () => new Set(latestTurnAssistantEntries.map(({ message }) => message.id)),
    [latestTurnAssistantEntries]
  )
  const activeProgressEntries = useMemo(
    () => latestTurnAssistantEntries.filter(({ message }) => message.type === 'progress_message'),
    [latestTurnAssistantEntries]
  )
  const activeResponseTextEntry = useMemo(
    () =>
      latestTurnAssistantEntries.find(({ message }) => message.id === activeResponseTextMessageId),
    [activeResponseTextMessageId, latestTurnAssistantEntries]
  )
  const latestTurnSupplementalEntries = useMemo(
    () =>
      latestTurnAssistantEntries.filter(
        ({ message }) =>
          message.type !== 'progress_message' && message.id !== activeResponseTextMessageId
      ),
    [activeResponseTextMessageId, latestTurnAssistantEntries]
  )
  const activeResponseCanContinueReveal = canContinueReveal(
    activeResponseTextEntry?.message ?? null,
    isLoading
  )
  const activeResponseRevealInProgress =
    !!activeResponseRevealState?.isRevealing &&
    (activeResponseRevealState.messageId === activeResponseTextMessageId ||
      activeResponseCanContinueReveal)
  const shouldReserveLingeringProgressSpace =
    activeProgressEntries.length === 0 &&
    activeResponseRevealInProgress &&
    lingeringActiveProgress?.turnUserMessageId === latestUserMessageId
  const displayedActiveProgressEntries = activeProgressEntries
  // CUR-206: detect interleaved progress + text in the latest turn — see
  // `detectInterleavedTurn` in the design module for the rule. When
  // interleaved, render entries in array order and treat the LAST progress
  // + LAST text as the "active" slots that cur-113's runway / min-height
  // anchors attach to. Earlier segments render inline. When NOT
  // interleaved, the original three-slot layout is used verbatim.
  const interleaveInfo = useMemo(
    () => detectInterleavedTurnFromEntries(latestTurnAssistantEntries),
    [latestTurnAssistantEntries]
  )
  const hasInterleavedAssistantEntries = interleaveInfo.isInterleaved
  const interleavedActiveProgressEntry = useMemo(() => {
    if (!hasInterleavedAssistantEntries) return null
    const idx = interleaveInfo.activeProgressIndex
    if (idx < 0) return null
    return latestTurnAssistantEntries.find(({ index }) => index === idx) ?? null
  }, [
    hasInterleavedAssistantEntries,
    interleaveInfo.activeProgressIndex,
    latestTurnAssistantEntries
  ])
  const interleavedActiveResponseEntry = useMemo(() => {
    if (!hasInterleavedAssistantEntries) return null
    const idx = interleaveInfo.activeResponseIndex
    if (idx < 0) return null
    return latestTurnAssistantEntries.find(({ index }) => index === idx) ?? null
  }, [
    hasInterleavedAssistantEntries,
    interleaveInfo.activeResponseIndex,
    latestTurnAssistantEntries
  ])
  const displayedActiveProgressMessageIds = useMemo(() => {
    if (hasInterleavedAssistantEntries) {
      // Only the LAST progress in the turn is "active" for styling (pulse,
      // active-cluster behavior). Earlier progress segments are completed and
      // render as static bubbles.
      return interleavedActiveProgressEntry
        ? new Set([interleavedActiveProgressEntry.message.id])
        : new Set<string>()
    }
    return new Set(displayedActiveProgressEntries.map(({ message }) => message.id))
  }, [
    displayedActiveProgressEntries,
    hasInterleavedAssistantEntries,
    interleavedActiveProgressEntry
  ])
  const pinnedActiveUserMessageIdRef = useRef<string | null>(null)

  const handleStreamingRevealStateChange = useCallback(
    (messageId: string, isRevealing: boolean) => {
      setActiveResponseRevealState(currentState =>
        currentState?.messageId === messageId && currentState.isRevealing === isRevealing
          ? currentState
          : { messageId, isRevealing }
      )
      setActiveResponseRevealRevision(revision => revision + 1)
    },
    []
  )

  useEffect(() => {
    if (activeProgressEntries.length > 0 && latestUserMessageId) {
      setLingeringActiveProgress({
        turnUserMessageId: latestUserMessageId,
        entries: activeProgressEntries
      })
      return
    }

    if (
      !activeResponseRevealInProgress ||
      lingeringActiveProgress?.turnUserMessageId !== latestUserMessageId
    ) {
      setLingeringActiveProgress(null)
    }
  }, [
    activeProgressEntries,
    activeResponseRevealInProgress,
    latestUserMessageId,
    lingeringActiveProgress?.turnUserMessageId
  ])

  useLayoutEffect(() => {
    if (!latestUserMessageId || activeRunwayUserMessageId === latestUserMessageId) return
    if (!latestUserMatchesReplacedActiveRunway) return

    pinnedActiveUserMessageIdRef.current = latestUserMessageId
    setActiveRunwayUserMessageId(latestUserMessageId)
  }, [activeRunwayUserMessageId, latestUserMatchesReplacedActiveRunway, latestUserMessageId])

  // ─────────────────────────────────────────────────────────────────────
  // Runway measurement lives in `chat/design/useTurnRunway.ts`. The hook
  // owns the PMV-slot min-height, the runway min-height, the response
  // runway padding, and the bottom spacer. It returns a branded
  // RunwayState so this component cannot accidentally substitute
  // literals. ALL the CUR-113 invariants (children-sum measurement,
  // per-child ResizeObserver, ref-based read of *Ref values, conditional
  // composer-overlap, no standalone trailing-gap-add) live there now.
  // ─────────────────────────────────────────────────────────────────────
  const runwayState = useTurnRunway({
    // `useLandingColumnLayout` is computed below this point in the
    // component body and isn't read by the runway tokens (only
    // `isPlugin` flips their values), so we omit it here rather than
    // reordering hooks.
    isPlugin,
    shouldReserveActiveTurnRunway,
    scrollContainer: messagesContainerRef,
    latestTurnRef: latestUserMessageRef,
    latestTurnUserMessageRef,
    activeProgressSlotRef,
    activeResponseSlotRef,
    composerPanelRef,
    latestUserMessageId,
    activeResponseRevealRevision,
    processedMessagesIdentity: processedMessages,
    isLoading,
    latestUserMatchesActiveRunway,
    composerPanelHeight
  })
  const {
    activeProgressSlotMinHeight,
    // `activeTurnRunwayMinHeight` is computed inside the hook and used
    // only for measurement-time decisions; render code doesn't consume
    // it directly. Kept on the branded RunwayState so the value is
    // still observable from tests / Storybook.
    activeResponseRunwayHeight,
    activeTurnBottomSpacerHeight,
    activeTurnRunwayReady
  } = runwayState
  const bottomComposerSpacerHeight = shouldReserveActiveTurnRunway
    ? (activeTurnBottomSpacerHeight ?? 0)
    : composerScrollSpacerHeight

  // CUR-210: the legacy in-place PMV-slot + runway measurement effects
  // (previously ~440 lines here) have moved into useTurnRunway above.

  const scrollLatestTurnIntoView = useCallback(
    (behavior: ScrollBehavior) => {
      // CUR-210: stamp user-scroll intent before smooth programmatic scrolls
      // so the RAF follow-along pin pauses for USER_SCROLL_INTENT_PAUSE_MS.
      if (behavior === 'smooth') {
        noteUserScrollIntent()
      }
      const latestTurn = latestUserMessageRef.current
      logChatScroll('scrollLatestTurnIntoView', {
        behavior,
        hasLatestTurn: !!latestTurn,
        ...snapshotScrollContainer(messagesContainerElRef.current)
      })
      latestTurn?.scrollIntoView({ behavior, block: 'start' })
    },
    [noteUserScrollIntent]
  )

  // Position the active turn synchronously before first paint. Completed chats
  // open at the bottom; active responses start with the latest user message
  // near the top, then fill the assistant message's minimum-height runway.
  useLayoutEffect(() => {
    if (initialScrollDoneRef.current) return
    if (!messages.length) return

    const savedPosition = sessionStorage.getItem(scrollPositionKey)
    if (savedPosition) {
      if (messagesContainerRef) {
        messagesContainerRef.scrollTop = parseInt(savedPosition, 10)
        initialScrollDoneRef.current = true
      }
      return
    }

    if (isLoading && latestUserMessageId && latestUserMessageRef.current) {
      if (!activeTurnRunwayReady) return
      pinnedActiveUserMessageIdRef.current = latestUserMessageId
      setActiveRunwayUserMessageId(latestUserMessageId)
      setActiveRunwayUserMessageContent(latestUserMessageContent)
      logChatScroll('initial.scrollLatestTurn', {
        latestUserMessageId,
        ...snapshotScrollContainer(messagesContainerElRef.current)
      })
      scrollLatestTurnIntoView('auto')
    } else if (messagesEndRef.current) {
      logChatScroll('initial.scrollEnd', {
        ...snapshotScrollContainer(messagesContainerElRef.current)
      })
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' })
    }
    initialScrollDoneRef.current = true
  }, [
    activeTurnRunwayReady,
    messages,
    scrollPositionKey,
    messagesContainerRef,
    isLoading,
    latestUserMessageId,
    latestUserMessageContent,
    scrollLatestTurnIntoView
  ])

  useLayoutEffect(() => {
    if (!isLoading || !latestUserMessageId || !latestUserMessageRef.current) return
    if (!activeTurnRunwayReady) return
    if (pinnedActiveUserMessageIdRef.current === latestUserMessageId) return
    if (latestUserMatchesReplacedActiveRunway) {
      pinnedActiveUserMessageIdRef.current = latestUserMessageId
      setActiveRunwayUserMessageId(latestUserMessageId)
      return
    }

    pinnedActiveUserMessageIdRef.current = latestUserMessageId
    setActiveRunwayUserMessageId(latestUserMessageId)
    setActiveRunwayUserMessageContent(latestUserMessageContent)
    userHasScrolledUpRef.current = false
    setShowScrollToBottom(false)
    logChatScroll('newTurn.queue-raf', {
      latestUserMessageId,
      ...snapshotScrollContainer(messagesContainerElRef.current)
    })
    // Defer the scroll so it runs after React has flushed the queued runway
    // state updates (paddingBottom on the assistant area + chat-response-runway
    // height) for this new turn. If we scrolled synchronously here, the DOM
    // would still reflect the previous turn's runway/spacer, scrollHeight would
    // be too small, and scrollIntoView would clamp to a position below
    // scrollPaddingTop — so the user message wouldn't fully reach the top.
    // requestAnimationFrame fires after React's render+commit cycle settles, so
    // by the time scrollIntoView runs, scrollHeight reflects the new runway.
    requestAnimationFrame(() => scrollLatestTurnIntoView('smooth'))
  }, [
    activeTurnRunwayReady,
    isLoading,
    latestUserMatchesReplacedActiveRunway,
    latestUserMessageContent,
    latestUserMessageId,
    scrollLatestTurnIntoView
  ])

  // `noteUserScrollIntent` is wired above (before scrollToBottom /
  // scrollLatestTurnIntoView) so programmatic smooth scrolls pause the
  // follow-along RAF pin. See `chat/design/useTurnScrollPin.ts`.

  // Do not bottom-yank during an active response. ChatGPT/Claude-style UX
  // keeps the user's prompt stable and lets the response grow below it.
  useEffect(() => {
    if (!initialScrollDoneRef.current) return
    if (isLoading) {
      logChatScroll('effect.bail-loading', { isLoading, latestUserMatchesActiveRunway })
      return
    }
    if (latestUserMatchesActiveRunway) {
      logChatScroll('effect.bail-active-runway', {
        latestUserMatchesActiveRunway,
        latestUserMessageId,
        activeRunwayUserMessageId,
        ...snapshotScrollContainer(messagesContainerElRef.current)
      })
      return
    }
    // Recompute live distance from the bottom — the cached ref is stale
    // whenever the response grew below the viewport without a scroll event,
    // and trusting it would yank a reading user down to the new bottom.
    if (updateUserScrolledUpFromCurrentLayout()) {
      logChatScroll('effect.bail-scrolled-up', {
        ...snapshotScrollContainer(messagesContainerElRef.current)
      })
      return
    }
    logChatScroll('effect.queue-throttled', {
      latestUserMessageId,
      activeRunwayUserMessageId,
      ...snapshotScrollContainer(messagesContainerElRef.current)
    })
    throttledScrollToBottom()
  }, [
    activeRunwayUserMessageId,
    isLoading,
    latestUserMatchesActiveRunway,
    latestUserMessageId,
    messages,
    throttledScrollToBottom,
    updateUserScrolledUpFromCurrentLayout
  ])

  // Get sidebar context for switching to tasks tab
  const { setActiveTab } = useSidebarContext()

  // Handle task link clicks - navigate to task view and switch sidebar to tasks tab
  const handleTaskLinkClick = useCallback(
    (taskId: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('task_id', taskId.toString())
      setSearchParams(newParams)
      // Switch sidebar to tasks tab for consistency
      setActiveTab('tasks')
    },
    [searchParams, setActiveTab, setSearchParams]
  )

  // Wrap onSendMessage to reset scroll state when user sends a message
  const handleSendMessage = useCallback(
    (...args: Parameters<typeof onSendMessage>) => {
      // Reset scroll tracking - user wants to see their message
      userHasScrolledUpRef.current = false
      setShowScrollToBottom(false)
      setSelectionWidgetSelections({})
      onSendMessage(...args)
    },
    [onSendMessage]
  )

  const nextComposerTaskRequest = useCallback((taskId: number) => {
    composerTaskRequestIdRef.current += 1
    return {
      taskId,
      requestId: composerTaskRequestIdRef.current
    }
  }, [])

  const requestComposerCodeReview = useCallback(
    (taskId: number) => {
      setCodeReviewTaskRequest(nextComposerTaskRequest(taskId))
    },
    [nextComposerTaskRequest]
  )

  const requestComposerFeedbackDraft = useCallback(
    (taskId: number) => {
      setFeedbackDraftRequest(nextComposerTaskRequest(taskId))
    },
    [nextComposerTaskRequest]
  )

  const {
    activeLandingArtifactTask,
    activeLandingArtifactTaskKickoffContent,
    activeRunningLandingArtifactTask,
    canShowRightPanel,
    getTaskPanelWidthBounds,
    handleTaskListSelect,
    handleTaskPanelOpen,
    handleTaskPanelResizeKeyDown,
    handleTaskPanelResizeStart,
    isTaskListOpen,
    isTaskPanelResizing,
    rightPanelWidthPx,
    setIsTaskListOpen,
    setIsTaskPanelMinimized,
    shouldRenderArtifactPanel,
    shouldRenderRightPanel,
    showCoderPanel,
    showDataWeavePanel,
    showDocumentationPanel,
    sortedConversationTasks,
    taskPanelSelectionVersion,
    useDesktopSplitPaneLayout
  } = useLandingArtifactPanel({
    chatLayoutRef,
    conversation,
    conversationUuid,
    editableCanvasOpen,
    handleTaskLinkClick,
    isPlugin,
    processedMessages,
    taskPanelLayoutRef
  })

  const canShowScratchPanel = !isPlugin && !!scratchCanvasItem && !editableCanvasOpen
  const showScratchPanel = canShowScratchPanel && isScratchPanelOpen
  const shouldRenderAnyRightPanel = shouldRenderRightPanel || showScratchPanel
  const shouldUseRightPanelOffset =
    shouldRenderAnyRightPanel && !useDesktopSplitPaneLayout && !showScratchPanel
  const rightPanelWidthStyle = showScratchPanel ? SCRATCH_WORKSPACE_PANEL_WIDTH : rightPanelWidthPx
  const useRightPanelFrameLayout = useDesktopSplitPaneLayout || showScratchPanel

  const messagePanelOffsetStyle = shouldUseRightPanelOffset
    ? {
        marginRight: `-${rightPanelWidthPx + 24}px`,
        paddingRight: `${rightPanelWidthPx + 24}px`
      }
    : undefined
  const inputPanelOffsetStyle = shouldUseRightPanelOffset
    ? {
        marginRight: `-${rightPanelWidthPx}px`,
        paddingRight: `${rightPanelWidthPx}px`
      }
    : undefined
  const composerPanelStyle = isPlugin
    ? inputPanelOffsetStyle
    : composerFrameStyle
      ? { ...composerFrameStyle, ...inputPanelOffsetStyle }
      : undefined
  const composerRoutingTask = activeRunningLandingArtifactTask ?? activeLandingArtifactTask
  const isLandingTaskChatPaused = !!activeRunningLandingArtifactTask
  const useSidePanelLayout = useDesktopSplitPaneLayout || showScratchPanel || editableCanvasOpen
  const shouldReserveMinimizedTaskPanelSpace =
    !isPlugin &&
    canShowRightPanel &&
    !!activeLandingArtifactTask &&
    !shouldRenderAnyRightPanel &&
    !editableCanvasOpen
  const useLandingColumnLayout = useSidePanelLayout || shouldReserveMinimizedTaskPanelSpace
  const hasCoderSummaryMessage = processedMessages.some(message => message.type === 'coder_summary')
  const useLandingChatSurface =
    useLandingColumnLayout || canShowRightPanel || hasCoderSummaryMessage
  const chatSurfaceBackgroundClassName =
    !isPlugin && useLandingChatSurface
      ? 'bg-tokens-neutral-bg-grey-2'
      : 'bg-tokens-neutral-bg-grey-1'
  const bottomScrollSpacerHeight = useLandingColumnLayout
    ? Math.max(
        bottomComposerSpacerHeight,
        composerScrollSpacerHeight,
        SIDE_PANEL_COMPOSER_SCROLL_SPACER_FLOOR_PX
      )
    : bottomComposerSpacerHeight
  const composerPanelBackgroundClassName = !isPlugin ? chatSurfaceBackgroundClassName : undefined
  const composerPanelVeilClassName = useLandingChatSurface
    ? 'from-tokens-neutral-bg-grey-2'
    : 'from-tokens-neutral-bg-grey-1'
  const composerPanelGutterClassName =
    !isPlugin && useLandingColumnLayout ? CHAT_SPLIT_VIEW_GUTTER_CLASS : 'px-0'
  const reservedMinimizedTaskChatWidth = `clamp(${MINIMIZED_TASK_CHAT_MIN_WIDTH_PX}px, calc(100% - ${rightPanelWidthPx}px), 100%)`
  const minimizedTaskChatFrameStyle: React.CSSProperties | undefined =
    shouldReserveMinimizedTaskPanelSpace
      ? {
          marginLeft: 'auto',
          marginRight: 'auto',
          maxWidth: '100%',
          width: reservedMinimizedTaskChatWidth
        }
      : undefined

  useLayoutEffect(() => {
    if (isPlugin) {
      setComposerFrameStyle(undefined)
      return
    }

    const composerFrame = messagesContainerRef ?? chatLayoutRef.current
    if (!composerFrame) return

    const updateComposerFrame = () => {
      const rect = composerFrame.getBoundingClientRect()
      const nextStyle: React.CSSProperties = {
        left: Math.round(rect.left),
        right: Math.round(window.innerWidth - rect.right)
      }

      setComposerFrameStyle(currentStyle => {
        if (currentStyle?.left === nextStyle.left && currentStyle?.right === nextStyle.right) {
          return currentStyle
        }

        return nextStyle
      })
    }

    updateComposerFrame()

    window.addEventListener('resize', updateComposerFrame)

    let resizeObserver: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateComposerFrame)
      resizeObserver.observe(composerFrame)
    }

    return () => {
      window.removeEventListener('resize', updateComposerFrame)
      resizeObserver?.disconnect()
    }
  }, [editableCanvasOpen, isPlugin, messagesContainerRef, useDesktopSplitPaneLayout])

  useLayoutEffect(() => {
    const composerPanel = composerPanelRef.current
    if (!composerPanel) return

    const updateComposerPanelHeight = () => {
      const nextHeight = Math.ceil(composerPanel.getBoundingClientRect().height)
      setComposerPanelHeight(currentHeight =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      )
    }

    updateComposerPanelHeight()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateComposerPanelHeight)
    resizeObserver.observe(composerPanel)

    return () => resizeObserver.disconnect()
  }, [
    composerFrameStyle?.left,
    composerFrameStyle?.right,
    editableCanvasOpen,
    isPlugin,
    useDesktopSplitPaneLayout
  ])

  // Clean up scroll position
  useEffect(() => {
    return () => {
      if (conversationUuid) {
        sessionStorage.removeItem(scrollPositionKey)
      }
    }
  }, [conversationUuid, scrollPositionKey])

  const renderMessages = () => {
    // Use processedMessages which has deduped editable canvas items. A
    // progress cluster is "active" iff it's the last progress_message in
    // the array AND no NEW user message follows it. The
    // `preLatestContinuationMessageIds` set excludes out-of-order or
    // prior-task-continuation user rows that briefly co-exist with the
    // current turn. See `chat/design/chatClusterSelectors.ts` for the
    // canonical rules.
    const lastProgressIndex = getLastProgressMessageIndex(processedMessages)
    const lastProgressFollowedByUser = hasUserMessageAfterLastProgress(processedMessages, {
      ignoreUserMessageIds: preLatestContinuationMessageIds
    })

    // Precompute feedback positions: find which messages are last in their assistant cluster
    // and what text message's feedback data to use
    const feedbackInfo = computeFeedbackPositions(processedMessages)
    // Compact-vs-desktop gap class lives in the design module. Compact
    // covers the plug-in panel AND the broader "landing column" layout
    // (split-pane, scratch panel, editable canvas, or a reserved
    // minimized-task-panel slot) so the entire chat stack agrees on one
    // vertical rhythm per surface.
    const latestTurnGapClassName = latestTurnGapClass({
      isPlugin,
      useLandingColumnLayout
    })
    const shouldReserveActiveProgressSlot =
      displayedActiveProgressEntries.length > 0 || shouldReserveLingeringProgressSpace
    const activeProgressSlotStyle: React.CSSProperties | undefined =
      shouldReserveActiveProgressSlot && activeProgressSlotMinHeight !== null
        ? { minHeight: activeProgressSlotMinHeight, flexShrink: 0 }
        : undefined
    const activeTurnRunwayStyle: React.CSSProperties = {
      scrollMarginTop: pinnedUserMessageTopGap,
      flexShrink: 0
    }
    const activeResponseSlotStyle: React.CSSProperties = {
      flexShrink: 0
    }
    const activeAssistantAreaStyle: React.CSSProperties = {
      paddingBottom: activeResponseRunwayHeight ?? 0
    }

    const renderChatMessage = (message: ChatMessage, index: number) => {
      // Only mark as active if it's the last progress message AND no user message follows it
      const isPriorTaskContinuationMessage = priorTaskContinuationMessageIds.has(message.id)
      const isLastProgressMessage =
        !isPriorTaskContinuationMessage &&
        ((message.type === 'progress_message' &&
          index === lastProgressIndex &&
          !lastProgressFollowedByUser) ||
          displayedActiveProgressMessageIds.has(message.id))
      const isLatestTurnMessage =
        message.id === latestUserMessageId ||
        latestTurnAssistantMessageIds.has(message.id) ||
        displayedActiveProgressMessageIds.has(message.id)

      const feedback = feedbackInfo.get(index)

      const messageProps = {
        message,
        isLoading: isLoading && isLatestTurnMessage,
        isInGracePeriod,
        isTaskInMessages: taskIdsInMessages.has(message.type === 'task' ? message.taskId : -1),
        isLastProgressMessage,
        feedbackMessage: feedback,
        allMessages: processedMessages,
        messageIndex: index,
        onTaskLinkClick: handleTaskLinkClick,
        onTaskPanelOpen: handleTaskPanelOpen,
        onFeedbackUpdate,
        onEditableCanvasClick: handleEditableCanvasClick,
        onSelectionWidgetChange: handleSelectionWidgetChange,
        selectionWidgetSelections,
        isSelectionWidgetDisabled:
          message.type === 'selection_widget' &&
          disabledSelectionWidgetIds.has(message.widget.widgetId),
        isSplitPaneLayout: useLandingColumnLayout,
        hiddenTaskKickoffId: useDesktopSplitPaneLayout
          ? activeLandingArtifactTask?.task_id
          : undefined,
        onStreamingRevealStateChange: handleStreamingRevealStateChange,
        onCoderTaskCodeReview: requestComposerCodeReview,
        onCoderTaskFeedbackDraft: requestComposerFeedbackDraft
      }

      if (message.type === 'text' && message.role === 'user') {
        return <UserMessage {...messageProps} />
      }

      if (message.type === 'text' && message.role === 'assistant') {
        return <AssistantResponse {...messageProps} />
      }

      return <ChatMessageContent {...messageProps} />
    }

    const renderMessageWrapper = (message: ChatMessage, index: number) => {
      return <div key={message.id}>{renderChatMessage(message, index)}</div>
    }

    return processedMessages.map((message, index) => {
      if (preLatestContinuationMessageIds.has(message.id)) return null
      if (latestTurnAssistantMessageIds.has(message.id)) return null

      const isLatestUserMessage = message.id === latestUserMessageId

      if (isLatestUserMessage) {
        return (
          <React.Fragment key={message.id}>
            {preLatestContinuationEntries.map(({ message, index }) =>
              renderMessageWrapper(message, index)
            )}
            <div
              ref={latestUserMessageRef}
              data-testid='chat-latest-turn'
              className={cm('flex flex-col', latestTurnGapClassName)}
              style={activeTurnRunwayStyle}
            >
              <div ref={latestTurnUserMessageRef} data-testid='chat-latest-turn-user-message'>
                {renderChatMessage(message, index)}
              </div>
              {shouldRenderLatestTurnAssistantArea && (
                <div
                  data-testid='chat-latest-turn-assistant-area'
                  className={cm('flex flex-col', latestTurnGapClassName)}
                  style={activeAssistantAreaStyle}
                >
                  {hasInterleavedAssistantEntries ? (
                    latestTurnAssistantEntries.map(({ message, index }) => {
                      const isActiveProgress =
                        interleavedActiveProgressEntry?.message.id === message.id
                      const isActiveResponse =
                        interleavedActiveResponseEntry?.message.id === message.id

                      if (isActiveProgress) {
                        return (
                          <div
                            key={message.id}
                            ref={activeProgressSlotRef}
                            data-testid='chat-active-progress-slot'
                            style={activeProgressSlotStyle}
                          >
                            {renderMessageWrapper(message, index)}
                          </div>
                        )
                      }

                      if (isActiveResponse) {
                        return (
                          <div
                            key={message.id}
                            ref={activeResponseSlotRef}
                            data-testid='chat-active-response-message'
                            className={cm('flex flex-col', latestTurnGapClassName)}
                            style={activeResponseSlotStyle}
                          >
                            {renderChatMessage(message, index)}
                          </div>
                        )
                      }

                      return renderMessageWrapper(message, index)
                    })
                  ) : (
                    <>
                      <div
                        ref={activeProgressSlotRef}
                        data-testid='chat-active-progress-slot'
                        style={activeProgressSlotStyle}
                        aria-hidden={displayedActiveProgressEntries.length > 0 ? undefined : true}
                      >
                        {displayedActiveProgressEntries.map(({ message, index }) =>
                          renderMessageWrapper(message, index)
                        )}
                      </div>
                      <div
                        ref={activeResponseSlotRef}
                        data-testid='chat-active-response-message'
                        className={cm('flex flex-col', latestTurnGapClassName)}
                        style={activeResponseSlotStyle}
                        aria-hidden={activeResponseTextEntry ? undefined : true}
                      >
                        {activeResponseTextEntry &&
                          renderChatMessage(
                            activeResponseTextEntry.message,
                            activeResponseTextEntry.index
                          )}
                      </div>
                      {latestTurnSupplementalEntries.map(({ message, index }) =>
                        renderMessageWrapper(message, index)
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </React.Fragment>
        )
      }

      return renderMessageWrapper(message, index)
    })
  }

  const composerPanel = (
    <div
      ref={composerPanelRef}
      className={cm(
        'relative flex shrink-0 justify-center',
        composerPanelBackgroundClassName,
        isPlugin
          ? 'absolute bottom-0 left-0 right-0 z-10 px-3 pb-3 pt-3'
          : cm('fixed bottom-0 z-[1] box-border pb-4 pt-0', composerPanelGutterClassName)
      )}
      style={composerPanelStyle}
    >
      {!isPlugin && (
        <div
          aria-hidden='true'
          data-testid='chat-composer-top-veil'
          className={cm(
            'pointer-events-none absolute inset-x-0 -top-10 z-0 h-10 bg-gradient-to-t to-transparent',
            composerPanelVeilClassName
          )}
        />
      )}
      <div
        className={cm(
          // Outer message-stack container. `box-content` so the
          // measured width matches the inner content (not content +
          // padding) — important for the runway-width math.
          'relative z-[1] min-w-0',
          isPlugin
            ? `flex-1 box-content ${MESSAGE_STACK_OUTER_PADDING_CLASS_PLUGIN}`
            : MESSAGE_STACK_OUTER_PADDING_CLASS_DESKTOP
        )}
      >
        {/* Scroll to bottom button */}
        {showScrollToBottom && (
          <button
            onClick={() => {
              userHasScrolledUpRef.current = false
              // Don't hide button here - let onScroll hide it when we reach the bottom
              scrollToBottom(true, true) // force = true, smooth = true
            }}
            className='absolute bottom-full left-1/2 z-10 mb-3 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white text-tokens-neutral-icon-solid shadow-segment transition-colors hover:bg-tokens-neutral-bg-white'
            aria-label='Scroll to bottom'
          >
            <svg
              width='12'
              height='12'
              viewBox='0 0 12 12'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              className='text-tokens-neutral-icon-solid'
            >
              <path
                d='M3 4.5L6 7.5L9 4.5'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        )}
        <ChatComposer
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          placeholder={inputPlaceholder}
          agentMode={agentMode}
          speedMode={speedMode}
          onAgentModeChange={setAgentMode}
          onSpeedModeChange={setSpeedMode}
          routingTask={composerRoutingTask}
          isTaskChatPaused={isLandingTaskChatPaused}
          codeReviewTaskRequest={codeReviewTaskRequest}
          feedbackDraftRequest={feedbackDraftRequest}
          selectionDraft={selectionWidgetDraft}
        />
      </div>
    </div>
  )

  const scratchAction =
    scratchCanvasItem && !isPlugin ? (
      <CustomTooltip content='Agent workspace' placement='bottom' delay={250}>
        <button
          type='button'
          onClick={handleScratchPanelToggle}
          aria-pressed={showScratchPanel}
          aria-label='Agent workspace'
          data-testid='scratch-files-button'
          className={cm(
            'inline-flex h-10 w-10 items-center justify-center rounded-[12px] border transition-colors',
            showScratchPanel
              ? 'border-tokens-status-link-2 bg-tokens-status-link-base-2 text-tokens-status-link-3'
              : 'border-tokens-neutral-border-1 bg-tokens-neutral-bg-white text-tokens-neutral-bg-action hover:bg-tokens-neutral-fill-fill-6'
          )}
        >
          <FolderOpen className='h-4 w-4' />
        </button>
      </CustomTooltip>
    ) : null
  const scratchCanvasItems = useMemo(
    () => (scratchCanvasItem ? [scratchCanvasItem] : []),
    [scratchCanvasItem]
  )

  // Chat View: Render normal chat interface
  return (
    <div className={cm('flex', chatSurfaceBackgroundClassName, 'h-full min-h-0')}>
      {!isPlugin && composerFrameStyle && typeof document !== 'undefined'
        ? createPortal(composerPanel, document.body)
        : null}
      {/* Main chat panel with optional task/canvas side panel */}
      <div
        ref={taskPanelLayoutRef}
        data-testid='chat-main-panel'
        className={cm(
          'flex min-w-0 transition-all duration-300 ease-out',
          useLandingColumnLayout ? 'h-full w-full' : 'flex-1 flex-col min-h-0'
        )}
      >
        <div
          className={cm(
            'flex min-w-0 flex-col',
            useLandingColumnLayout
              ? editableCanvasOpen
                ? 'h-full min-w-0 flex-1'
                : 'h-full min-w-[520px] flex-1'
              : 'flex-1 min-h-0'
          )}
        >
          {/* Header */}
          <ChatDisplayHeader
            conversationTitle={conversationTitle}
            conversationSubtitle={conversationSubtitle}
            isSplitPaneLayout={useLandingColumnLayout}
            useTaskPanelHeaderLayout={canShowRightPanel || showScratchPanel || editableCanvasOpen}
            scratchAction={scratchAction}
            taskListAction={
              !isPlugin ? (
                <LandingTaskListMenu
                  activeTaskId={activeLandingArtifactTask?.task_id}
                  isOpen={isTaskListOpen}
                  onSelectTask={handleTaskListSelect}
                  onToggle={() => setIsTaskListOpen(open => !open)}
                  tasks={sortedConversationTasks}
                />
              ) : null
            }
          />
          <div
            ref={chatLayoutRef}
            data-testid='chat-layout-frame'
            style={minimizedTaskChatFrameStyle}
            className={cm(
              'relative flex min-h-0 flex-1',
              shouldReserveMinimizedTaskPanelSpace && 'self-center',
              isPlugin ? 'p-3' : useLandingColumnLayout ? 'p-0' : 'p-6'
            )}
          >
            <div className='relative flex-1 flex flex-col min-w-0 min-h-0'>
              <div
                className={cm(
                  'self-stretch flex-1 overflow-auto flex justify-center',
                  !isPlugin && useLandingChatSurface && 'bg-tokens-neutral-bg-grey-2',
                  isPlugin
                    ? 'm-[-12px] p-[12px] mb-0 pb-0'
                    : useLandingColumnLayout
                      ? `${CHAT_SPLIT_VIEW_GUTTER_CLASS} py-2 pb-0`
                      : 'm-[-24px] p-[24px] mb-0 pb-0'
                )}
                style={{
                  ...messagePanelOffsetStyle,
                  overflowAnchor: 'none',
                  scrollPaddingTop: pinnedUserMessageTopGap
                }}
                ref={setMessagesContainerRef}
                onScroll={e => {
                  sessionStorage.removeItem(scrollPositionKey)
                  const target = e.currentTarget

                  // Load older messages when scrolled near the top
                  if (target.scrollTop < 200 && hasMoreMessages && !isLoadingMore) {
                    const prevHeight = target.scrollHeight
                    loadMoreMessages().then(() => {
                      // Preserve scroll position after prepending older messages
                      requestAnimationFrame(() => {
                        target.scrollTop = target.scrollHeight - prevHeight
                      })
                    })
                  }

                  // Track if user has scrolled away from bottom. The
                  // BOTTOM_PROXIMITY_THRESHOLD_PX value lives in the design
                  // module so this handler stays in sync with the
                  // throttled / RAF auto-scroll paths above.
                  const distanceFromBottom =
                    target.scrollHeight - target.scrollTop - target.clientHeight
                  const hasScrolledUp = distanceFromBottom > BOTTOM_PROXIMITY_THRESHOLD_PX
                  userHasScrolledUpRef.current = hasScrolledUp
                  setShowScrollToBottom(hasScrolledUp)
                }}
              >
                <div
                  className={cm(
                    'flex flex-col break-words min-w-0',
                    // Gap class uses the same surface-aware token as the
                    // latest-turn cluster wrapper above so the entire chat
                    // stack agrees on a single vertical rhythm. Outer
                    // padding pulls from the same design module so the
                    // ESLint rule can enforce single-source-of-truth.
                    latestTurnGapClass({ isPlugin, useLandingColumnLayout }),
                    // Width pulls from the same outer-padding token in
                    // every non-plug-in case (landing-column included)
                    // — the slash-commands PR collapsed the
                    // landing-vs-desktop column-width split.
                    isPlugin
                      ? `flex-1 ${MESSAGE_STACK_OUTER_PADDING_CLASS_PLUGIN}`
                      : MESSAGE_STACK_OUTER_PADDING_CLASS_DESKTOP
                  )}
                >
                  {isLoadingMore && (
                    <div className='flex items-center justify-center py-3 text-tokens-neutral-text-tertiary'>
                      <svg
                        className='animate-spin h-4 w-4 mr-2'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        />
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        />
                      </svg>
                      <Typography
                        fontFamily='geist'
                        fontSize='bodySm'
                        className='text-tokens-neutral-text-tertiary'
                      >
                        Loading older messages...
                      </Typography>
                    </div>
                  )}
                  {renderMessages()}

                  <div
                    data-testid='chat-response-runway'
                    style={{ height: bottomScrollSpacerHeight, flexShrink: 0 }}
                    aria-hidden='true'
                  />
                  {/* Scroll target for reliable bottom scrolling */}
                  <div ref={messagesEndRef} />
                </div>
              </div>
              {isPlugin ? composerPanel : null}
            </div>
          </div>
        </div>
        {/* Tasks panel - only show when split-screen is NOT open and not in plugin */}
        {shouldRenderAnyRightPanel && (
          <div
            className={cm(
              'min-w-0 shrink-0 overflow-hidden transition-[width,opacity,transform] duration-300 ease-out motion-safe:animate-fade-slide-in',
              useRightPanelFrameLayout && 'p-4',
              isTaskPanelResizing && 'transition-none'
            )}
            style={{
              width: rightPanelWidthStyle
            }}
            data-testid='landing-task-side-panel'
          >
            <div
              className={cm(
                'relative flex min-h-0 flex-col',
                useRightPanelFrameLayout ? 'h-full' : 'sticky top-6 h-[calc(100vh-48px-48px)]'
              )}
            >
              {shouldRenderArtifactPanel && !showScratchPanel && (
                <div
                  role='separator'
                  aria-label='Resize task side panel'
                  aria-orientation='vertical'
                  aria-valuemin={getTaskPanelWidthBounds().min}
                  aria-valuemax={getTaskPanelWidthBounds().max}
                  aria-valuenow={rightPanelWidthPx}
                  tabIndex={0}
                  onMouseDown={handleTaskPanelResizeStart}
                  onKeyDown={handleTaskPanelResizeKeyDown}
                  className='group/resize absolute -left-3 top-0 z-30 flex h-full w-3 cursor-col-resize touch-none select-none items-stretch justify-center outline-none'
                  data-testid='landing-task-panel-resize-handle'
                  title='Drag to resize task panel'
                >
                  <div
                    className={cm(
                      'my-4 w-px rounded-full transition-colors',
                      isTaskPanelResizing
                        ? 'bg-tokens-status-link-3'
                        : 'bg-transparent group-hover/resize:bg-tokens-neutral-border-colored group-focus-visible/resize:bg-tokens-status-link-3'
                    )}
                  />
                </div>
              )}
              <div className='min-h-0 flex-1'>
                {showScratchPanel && scratchCanvasItem ? (
                  <CanvasFileViewer
                    key={scratchCanvasItem.s3FilePath || scratchCanvasItem.title}
                    canvasItems={scratchCanvasItems}
                    onClose={() => setIsScratchPanelOpen(false)}
                    className='h-full w-full rounded-2xl border border-tokens-neutral-border-1 shadow-none'
                    sidebarTitle='Agent Workspace'
                  />
                ) : showCoderPanel && activeLandingArtifactTask && conversationUuid ? (
                  <LandingCoderTaskPanel
                    conversationUuid={conversationUuid}
                    onClose={() => setIsTaskPanelMinimized(true)}
                    openRequestId={taskPanelSelectionVersion}
                    kickoffContent={activeLandingArtifactTaskKickoffContent}
                    workspaceUuid={workspaceUuid}
                    task={activeLandingArtifactTask}
                  />
                ) : showDataWeavePanel && activeLandingArtifactTask && conversationUuid ? (
                  <LandingDataWeaveTaskPanel
                    conversationUuid={conversationUuid}
                    onClose={() => setIsTaskPanelMinimized(true)}
                    openRequestId={taskPanelSelectionVersion}
                    task={activeLandingArtifactTask}
                  />
                ) : showDocumentationPanel && activeLandingArtifactTask && conversationUuid ? (
                  <LandingDocumentationTaskPanel
                    conversationUuid={conversationUuid}
                    onClose={() => setIsTaskPanelMinimized(true)}
                    openRequestId={taskPanelSelectionVersion}
                    task={activeLandingArtifactTask}
                  />
                ) : conversation ? (
                  <ConversationTaskListCard conversation={conversation} />
                ) : null}
              </div>
            </div>
          </div>
        )}
        {editableCanvasOpen && selectedEditableCanvas && (
          <div
            className='box-border min-w-[360px] shrink-0 p-4'
            style={{
              width: 'clamp(360px, 48vw, 744px)'
            }}
          >
            <div className='h-full min-h-0 overflow-hidden rounded-2xl border border-tokens-neutral-border-1 bg-tokens-neutral-bg-white shadow-heavy'>
              <EditableCanvasSplitScreen
                canvas={selectedEditableCanvas}
                onClose={handleCloseSplitScreen}
                onSelectionFeedback={handleCanvasSelectionFeedback}
                isProcessing={processingCanvasPaths.has(selectedEditableCanvas.s3FilePath)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Wrap ChatDisplay with the lifted BookmarkEditorProvider so every
// SaveAsBookmarkButton in the conversation shares one modal instance
// (and one set of mutation hooks) instead of mounting N copies — see
// BookmarksPopover.tsx for the rationale.
const ChatDisplayWithBookmarkEditor: React.FC<ChatDisplayProps> = props => (
  <BookmarkEditorProvider>
    <ChatDisplay {...props} />
  </BookmarkEditorProvider>
)

export default ChatDisplayWithBookmarkEditor
